import React, { useState, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import { Box,Container,Typography,Grid,Card,Button,Chip,List,ListItem,ListItemIcon,ListItemText,Dialog,DialogTitle,DialogContent,IconButton,Rating,Avatar,Breadcrumbs,Link} from '@mui/material';
import {CheckCircle,Close,Mic,Campaign,Psychology,School,EmojiEvents,Star,RecordVoiceOver,NavigateNext,Home} from '@mui/icons-material';
import ContactUsForm from '../components/ContactForm';
import banner02 from '../assets/images/banner02.webp';
import speechImage from '../assets/images/Courses2.webp';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';

const courseFeatures = [
  {
    icon: <Mic sx={{ color: '#9f2122', fontSize: '2rem' }} />,
    title: 'Pronunciation Mastery',
    description: 'Master standard Mandarin pronunciation with tone accuracy and clarity'
  },
  {
    icon: <RecordVoiceOver sx={{ color: '#9f2122', fontSize: '2rem' }} />,
    title: 'Performance Skills',
    description: 'Develop confidence through drama, recitation, and stage presence training'
  },
  {
    icon: <Campaign sx={{ color: '#9f2122', fontSize: '2rem' }} />,
    title: 'Public Speaking',
    description: 'Learn effective presentation techniques and audience engagement'
  },
  {
    icon: <Psychology sx={{ color: '#9f2122', fontSize: '2rem' }} />,
    title: 'Confidence Building',
    description: 'Overcome speaking anxiety and build self-assurance in communication'
  },
  {
    icon: <School sx={{ color: '#9f2122', fontSize: '2rem' }} />,
    title: 'Cultural Expression',
    description: 'Understand and express Chinese cultural nuances through speech'
  },
  {
    icon: <EmojiEvents sx={{ color: '#9f2122', fontSize: '2rem' }} />,
    title: 'Competition Prep',
    description: 'Prepare for speech competitions and public speaking events'
  }
];

const curriculumModules = [
  {
    title: 'Foundation Level (Beginner)',
    duration: '8-12 sessions',
    description: 'Perfect for beginners and young learners starting their speech journey',
    content: [
      'Basic Mandarin pronunciation and tone training',
      'Simple poems and short story recitation',
      'Voice projection and breathing techniques',
      'Basic stage presence and posture',
      'Confidence building exercises',
      'Introduction to Chinese cultural expressions'
    ]
  },
  {
    title: 'Intermediate Level',
    duration: '12-16 sessions',
    description: 'Designed for learners with basic Mandarin knowledge wanting to improve speaking skills',
    content: [
      'Advanced pronunciation refinement',
      'Classical Chinese poetry recitation',
      'Storytelling and narrative techniques',
      'Presentation skills and structure',
      'Emotional expression in speech',
      'Cultural performance elements'
    ]
  },
  {
    title: 'Advanced Level',
    duration: '16-20 sessions',
    description: 'For experienced speakers preparing for competitions or professional presentations',
    content: [
      'Eloquence and persuasive speaking',
      'Advanced performance techniques',
      'Competition speech preparation',
      'Professional presentation skills',
      'Cross-cultural communication',
      'Leadership communication skills'
    ]
  },
  {
    title: 'Specialized Programs',
    duration: 'Customizable',
    description: 'Tailored programs for specific needs and goals',
    content: [
      'Business presentation skills',
      'Academic conference speaking',
      'Wedding and celebration speeches',
      'Competition coaching',
      'Professional voice training',
      'Personal development coaching'
    ]
  }
];

const testimonials = [
  {
    name: 'Sarah Chen',
    role: 'Parent of 8-year-old student',
    rating: 5,
    text: 'My daughter\'s confidence has soared since joining the speech program. She now speaks Mandarin with such clarity and performs beautifully at school events.',
    avatar: '/api/placeholder/60/60'
  },
  {
    name: 'David Liu',
    role: 'Business Professional',
    rating: 5,
    text: 'The advanced program helped me present confidently to Chinese clients. The cultural nuances I learned have been invaluable for my career.',
    avatar: '/api/placeholder/60/60'
  },
  {
    name: 'Emily Wang',
    role: 'University Student',
    rating: 5,
    text: 'Won first place in the university Mandarin speech competition after completing this course. The training was comprehensive and effective.',
    avatar: '/api/placeholder/60/60'
  }
];

function SpeechEloquence() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);

  // SEO Meta Tags Effect
  useEffect(() => {
    // Update page title and meta description for SEO
    document.title = 'Speech & Eloquence in Mandarin - CHIHUI Mandarin | Singapore';
    
    // Create or update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Master Mandarin speech and eloquence with our specialized programs in Singapore. Build confidence, improve pronunciation, and excel in public speaking. Perfect for all ages.';
    
    // Create or update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = 'Mandarin speech, Chinese eloquence, public speaking Singapore, Mandarin pronunciation, Chinese recitation, speech training, presentation skills, confidence building';
    
    // Cleanup function to restore original title
    return () => {
      document.title = 'CHIHUI Mandarin - Professional Chinese Language Learning in Singapore';
    };
  }, []);

  return (
    <>
      <SEOHead pageKey="speech-eloquence" />
      <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>

    {/* Header Section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '240px', md: '500px' },
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Background Image with Overlay */}
        <Box
          component="img"
          src={banner02}
          alt="Business Chinese Banner"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0
          }}
        />
        
        {/* Content */}
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            minHeight: { xs: '240px', md: '500px' },
            pt: 0,
            px: { xs: 1, md: 4 }
          }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12}>
                <Box sx={{ 
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 60,
                    left: -20,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    zIndex: -1
                  }
                }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '1.5rem',
                      lineHeight: 1.6,
                      letterSpacing: '0.1em',
                      mb: 4,
                      fontWeight: 500,
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      display: { xs: 'none', md: 'block' }
                    }}
                  >
                    Experience our teaching quality with a complimentary trial session
                  </Typography>
                  <Typography
                    variant="h1"
                    sx={{
                      fontFamily: 'serif',
                      fontWeight: 700,
                      color: 'white',
                      mb: 5,
                      fontSize: { xs: '2.25rem', md: '4rem' },
                      lineHeight: 1.1,
                      textShadow: '2px 2px 8px rgba(0,0,0,0.7)'
                    }}
                  >
                     Speech & Eloquence
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => setEnrollDialogOpen(true)}
                      sx={{
                        bgcolor: '#9f2122',
                        color: 'white',
                        px: 6,
                        py: 2,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        borderRadius: 10,
                        textTransform: 'none',
                        boxShadow: '0 4px 20px rgba(159, 33, 34, 0.4)',
                        '&:hover': {
                          bgcolor: '#8a1e1f',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 25px rgba(159, 33, 34, 0.5)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Book Free Trial
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Breadcrumb Navigation */}
      <Box sx={{ 
        bgcolor: 'white', 
        py: 2, 
        borderBottom: '1px solid rgba(0,0,0,0.05)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <Container maxWidth="xl">
          <Breadcrumbs 
            separator={<NavigateNext fontSize="small" />} 
            aria-label="breadcrumb"
            sx={{
              '& .MuiBreadcrumbs-separator': {
                color: '#5a6c7d'
              }
            }}
          >
             <Link
               underline="hover"
               sx={{ display: 'flex', alignItems: 'center'}}
               href="/"
             >
               <Home sx={{ mr: 0.5 }} fontSize="inherit" />
               Home
             </Link>
             <Link
               underline="hover"
               href="/courses"
             >
               Courses
             </Link>
             <Typography>
               Speech & Eloquence
             </Typography>
          </Breadcrumbs>
        </Container>
      </Box>

      {/* Course Overview Section */}
      <Box id="overview" sx={{ py: { xs: 6, md: 12 }, bgcolor: 'white' }}>
        <Container maxWidth="xl">
          <Grid container spacing={ { xs: 4, md: 8 }}>
            <Grid item xs={12} md={5}>
              <Box
                component="img"
                src={speechImage}
                alt="Speech & Eloquence Course"
                sx={{
                  width: '100%',
                  height: 'auto',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontFamily: 'serif',
                    fontWeight: 700,
                    color: '#07284b',
                    mb: 3,
                    fontSize: { xs: '2rem', md: '2.5rem' }
                  }}
                >
                  Speech & Eloquence
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    color: '#9f2122',
                    mb: 4,
                    fontWeight: 600,
                    fontSize: { xs: '1.2rem', md: '1.5rem' }
                  }}
                >
                  Confident Communication & Public Speaking Mastery
                </Typography>
                
                <Box sx={{ mb: { xs: 2, md: 4 }, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<School />}
                    label="Target: Children, Professionals, Public Speakers"
                    sx={{
                      bgcolor: 'rgba(7, 40, 75, 0.1)',
                      color: '#07284b',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                    }}
                  />
                  <Chip
                    icon={<Star />}
                    label="Level: Beginner to Advanced"
                    sx={{
                      bgcolor: 'rgba(7, 40, 75, 0.1)',
                      color: '#07284b',
                      fontWeight: 600,
                      fontSize: '0.9rem'
                    }}
                  />
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#5a6c7d',
                    mb: { xs: 2, md: 4 },
                    lineHeight: 1.8,
                    fontSize: '1.1rem'
                  }}
                >
                  Transform your Mandarin speaking abilities through our comprehensive speech and eloquence program. Build confidence, master pronunciation, and excel in presentations with traditional Chinese oratory arts combined with modern communication techniques.
                </Typography>
                
                <Typography
                  variant="body1"
                  sx={{
                    color: '#5a6c7d',
                    mb: { xs: 2, md: 3 },
                    lineHeight: 1.8,
                    fontSize: '1.1rem'
                  }}
                >
                  Our Speech & Eloquence program is designed for learners who want to develop confident and articulate Mandarin speaking skills. Whether you're preparing for presentations, competitions, or professional speaking engagements, our course provides comprehensive training in pronunciation, stage presence, and cultural expression.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: '#5a6c7d',
                    mb: 3,
                    lineHeight: 1.8,
                    fontSize: '1.1rem'
                  }}
                >
                  From children learning recitation and performance skills to professionals mastering presentation techniques, our program builds confidence through structured practice, personalized feedback, and performance opportunities that help you express yourself authentically in Mandarin.
                </Typography>
                
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Course Content Section */}
      <Box 
        id="curriculum"
        sx={{ 
          py: { xs: 6, md: 12 }, 
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                color: '#07284b',
                mb: 3,
                fontSize: { xs: '2.2rem', md: '2.8rem' }
              }}
            >
              Comprehensive Curriculum
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#5a6c7d',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: '1.1rem'
              }}
            >
              Progressive learning path from basic pronunciation to advanced eloquence
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {curriculumModules.map((module, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    border: 'none',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: index === 0 
                        ? 'linear-gradient(90deg, #3b82f6, #1d4ed8)'
                        : index === 1 
                        ? 'linear-gradient(90deg, #f59e0b, #d97706)'
                        : index === 2
                        ? 'linear-gradient(90deg, #10b981, #059669)'
                        : 'linear-gradient(90deg, #9f2122, #7c1d1e)',
                      zIndex: 1
                    },
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 25px rgba(0, 0, 0, 0.1)',
                      '& .level-icon': {
                        transform: 'scale(1.1) rotate(5deg)'
                      }
                    }
                  }}
                >
                  <Box
                    sx={{
                      p: { xs: 2, md: 3 },
                      background: index === 0 
                        ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                        : index === 1 
                        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                        : index === 2
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        : 'linear-gradient(135deg, #9f2122 0%, #7c1d1e 100%)',
                      color: 'white',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -50,
                        right: -50,
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.1)',
                        zIndex: 0
                      }
                    }}
                  >
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          className="level-icon"
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            mr: 2,
                            transition: 'all 0.3s ease'
                          }}
                        >
                          {index === 0 ? <School /> : index === 1 ? <Mic /> : index === 2 ? <EmojiEvents /> : <RecordVoiceOver />}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 700,
                              mb: 0.5,
                              fontSize: '1.4rem',
                              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}
                          >
                            {module.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              opacity: 0.9,
                              fontSize: '0.9rem',
                              fontWeight: 500
                            }}
                          >
                            Duration: {module.duration}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ p: 4, bgcolor: 'white' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1e293b',
                        fontWeight: 600,
                        mb: 3,
                        fontSize: '1.1rem',
                        lineHeight: 1.4
                      }}
                    >
                      Focus Areas
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        mb: 3,
                        fontSize: '0.95rem',
                        lineHeight: 1.5
                      }}
                    >
                      {module.description}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#475569',
                        fontWeight: 600,
                        mb: 2,
                        fontSize: '0.9rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Key Topics
                    </Typography>
                    <Box>
                      {module.content.slice(0, 4).map((topic, topicIndex) => (
                        <Box key={topicIndex} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: index === 0 
                                ? '#3b82f6'
                                : index === 1 
                                ? '#f59e0b'
                                : index === 2
                                ? '#10b981'
                                : '#9f2122',
                              mt: 0.75,
                              mr: 2,
                              flexShrink: 0
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#64748b',
                              fontSize: '0.9rem',
                              lineHeight: 1.5
                            }}
                          >
                            {topic}
                          </Typography>
                        </Box>
                      ))}
                      {module.content.length > 4 && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#9f2122',
                            fontSize: '0.85rem',
                            fontStyle: 'italic',
                            mt: 1
                          }}
                        >
                          +{module.content.length - 4} more topics
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>


      {/* Key Features Section */}
      <Box sx={{ py: { xs: 6, md: 12 }, bgcolor: 'white' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                color: '#07284b',
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              Why Choose Our Speech Program?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#5a6c7d',
                maxWidth: 800,
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: '1.2rem'
              }}
            >
              Our comprehensive approach combines traditional Chinese oratory arts with modern presentation techniques
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {courseFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 4,
                    border: '1px solid rgba(0,0,0,0.08)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  <Box sx={{ mb: 3, display: { xs: 'none', md: 'block' } }}>
                    {feature.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#07284b',
                      fontWeight: 600,
                      mb: 2,
                      fontSize: '1.3rem'
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#5a6c7d',
                      lineHeight: 1.6
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box sx={{ py: { xs: 6, md: 12 }, background: 'wlinear-gradient(135deg, rgba(7, 40, 75, 0.02) 0%, rgba(159, 33, 34, 0.01) 100%)' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                color: '#07284b',
                mb: 2,
                fontSize: { xs: '1.8rem', md: '2.2rem' }
              }}
            >
              Learning Options
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#5a6c7d',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Choose the learning format that best suits your style and goals
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {/* Online Classes */}
            <Grid item xs={12} md={5}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 4,
                  border: '2px solid rgba(7, 40, 75, 0.1)',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                    borderColor: '#07284b'
                  }
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: '#07284b',
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '1.5rem', md: '1.8rem' }
                  }}
                >
                  Online Learning
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#5a6c7d',
                    mb: 3,
                    fontSize: '1rem'
                  }}
                >
                  From $70+ per session
                </Typography>
                
                <List>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Interactive video sessions" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Digital materials included" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Flexible scheduling" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Recording available" />
                  </ListItem>
                </List>
              </Card>
            </Grid>

            {/* In-Person Classes */}
            <Grid item xs={12} md={5}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 4,
                  border: '2px solid #9f2122',
                  position: 'relative',
                  bgcolor: 'rgba(159, 33, 34, 0.02)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(159, 33, 34, 0.2)'
                  }
                }}
              >
                <Chip
                  label="Most Popular"
                  sx={{
                    position: 'absolute',
                    top:-8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: '#9f2122',
                    color: 'white',
                    fontWeight: 600
                  }}
                />
                
                <Typography
                  variant="h4"
                  sx={{
                    color: '#07284b',
                    fontWeight: 700,
                    mb: 1,
                    mt: 2,
                    fontSize: { xs: '1.5rem', md: '1.8rem' }
                  }}
                >
                  Face-to-Face Learning
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#5a6c7d',
                    mb: 3,
                    fontSize: '1rem'
                  }}
                >
                  From $90+ per session
                </Typography>
                
                <List>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Face-to-face instruction" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Physical materials provided" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Performance opportunities" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Immediate feedback" />
                  </ListItem>
                </List>
              </Card>
            </Grid>

          </Grid>

          {/* Special Offers */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Card
              sx={{
                p: 4,
                background: 'linear-gradient(135deg, rgba(7, 40, 75, 0.05) 0%, rgba(159, 33, 34, 0.03) 100%)',
                border: '1px solid rgba(159, 33, 34, 0.2)',
                borderRadius: 3,
                maxWidth: 800,
                mx: 'auto'
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: '#07284b',
                  fontWeight: 600,
                  mb: 2
                }}
              >
                Special Package Offers
              </Typography>
              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} sm={6}>
                  <Chip
                    label="8-session program: 10% discount"
                    sx={{
                      bgcolor: '#9f2122',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      py: 2,
                      px: 1
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Chip
                    label="Free trial session available"
                    sx={{
                      bgcolor: '#07284b',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      py: 2,
                      px: 1
                    }}
                  />
                </Grid>
              </Grid>
            </Card>
          </Box>
        </Container>
      </Box>

      {/* Student Testimonials */}
      <Box 
        sx={{ 
          py: { xs: 6, md: 12 }, 
          bgcolor: 'white'
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                color: '#07284b',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '3rem' }
              }}
            >
              Student Success Stories
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#5a6c7d',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Hear from our students who have transformed their speaking abilities
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    p: 4,
                    borderRadius: 4,
                    border: '1px solid rgba(0,0,0,0.08)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Star 
                    sx={{ 
                      color: '#9f2122', 
                      fontSize: '2rem', 
                      mb: 2,
                      opacity: 0.7,
                      display: { xs: 'none', md: 'block' }
                    }} 
                  />
                  
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#5a6c7d',
                      lineHeight: 1.6,
                      mb: 3,
                      fontStyle: 'italic'
                    }}
                  >
                    "{testimonial.text}"
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      sx={{ width: 48, height: 48 }}
                    />
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: '#07284b',
                          fontWeight: 600
                        }}
                      >
                        {testimonial.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#5a6c7d',
                          fontSize: '0.85rem'
                        }}
                      >
                        {testimonial.role}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ mt: 2 }}>
                    <Rating 
                      value={testimonial.rating} 
                      readOnly 
                      sx={{ color: '#9f2122' }}
                    />
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box
        sx={{
          py: { xs: 6, md: 12 },
          background: 'linear-gradient(135deg, #07284b 0%, #9f2122 100%)',
          color: 'white'
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2.2rem', md: '2.8rem' }
              }}
            >
              Ready to Transform Your Speaking Skills?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                opacity: 0.9,
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Join our Speech & Eloquence program and discover the confidence to speak Mandarin with clarity, 
              poise, and cultural authenticity.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => setEnrollDialogOpen(true)}
                sx={{
                  bgcolor: 'white',
                  color: '#07284b',
                  px: 6,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  borderRadius: 3,
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.9)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 25px rgba(0,0,0,0.3)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Start Free Trial Today
              </Button>
              
              <Button
                variant="outlined"
                size="large"
                href="tel:+6589208197"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 6,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Call +65 89208197
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Enroll Dialog */}
      <Dialog
        open={enrollDialogOpen}
        onClose={() => setEnrollDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isSmallScreen}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: {xs: 2, md: 4},
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #07284b 0%, #9f2122 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2,
            px: 3
          }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: 'serif',
              fontWeight: 700,
              fontSize: { xs: '1.3rem', md: '1.5rem' }
            }}
          >
            Enroll in Speech & Eloquence Program
          </Typography>
          <IconButton
            onClick={() => setEnrollDialogOpen(false)}
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#9f2122',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#8a1e1f'
            }
          }}
        >
          <Box sx={{ px: 4, py: { xs: 4, md: 8 } }}>
            <ContactUsForm />
          </Box>
        </DialogContent>
      </Dialog>
      </Box>
    </>
  );
}

export default SpeechEloquence;
